/**
 * Feature detection for the RendPleX AI Advisor.
 *
 * The system prompt instructs the AI to route users to the appropriate
 * RendPleX tool via **bolded feature names** (Markdown). We detect those
 * mentions here and turn them into tap-to-open chips rendered below the
 * assistant bubble. Detection is purely text-based so it works in EN + FR
 * and across streaming/final states.
 *
 * Each entry maps a stable feature id → routing info + label keys.
 * The `patterns` list intentionally covers both EN and FR variants
 * (case-insensitive) so the same detector serves both languages.
 */

// Feature registry. `route(dealId)` produces the target URL; if a deal_id
// is known we deep-link the tool to THIS property, otherwise we just open it.
export const ADVISOR_FEATURES = {
  scenario_analyzer: {
    labelKey: 'aiAdvisor.chips.scenarioAnalyzer',
    route: (dealId) => (dealId ? `/scenario?deal_id=${encodeURIComponent(dealId)}` : '/scenario'),
    patterns: [/scenario analyzer/i, /analyseur de sc[ée]narios?/i],
  },
  deal_analyzer: {
    labelKey: 'aiAdvisor.chips.dealAnalyzer',
    // /analyze is the blank analyzer; when we know the deal, the actual
    // per-deal analysis lives at /deal/:dealId which is already loaded.
    route: (dealId) => (dealId ? `/deal/${encodeURIComponent(dealId)}` : '/analyze'),
    patterns: [/deal analyzer/i, /analyseur de propri[ée]t[ée]/i, /analyseur d'affaire/i],
  },
  deal_comparison: {
    labelKey: 'aiAdvisor.chips.dealComparison',
    route: () => '/compare',
    patterns: [/deal comparison/i, /comparaison de deals?/i, /comparaison de propri[ée]t[ée]s?/i],
  },
  portfolio_doctor: {
    labelKey: 'aiAdvisor.chips.portfolioDoctor',
    route: () => '/portfolio-doctor',
    patterns: [/portfolio doctor/i],
  },
  portfolio_dashboard: {
    labelKey: 'aiAdvisor.chips.portfolioDashboard',
    route: () => '/portfolio',
    // Order matters — this MUST come after portfolio_doctor detection so
    // "Portfolio Doctor" doesn't accidentally match the dashboard pattern.
    patterns: [/portfolio dashboard/i, /tableau de bord du portefeuille/i],
  },
  portfolio_goals: {
    labelKey: 'aiAdvisor.chips.portfolioGoals',
    // Portfolio Goals is embedded inside the Portfolio Health dashboard.
    route: () => '/portfolio/health',
    patterns: [/portfolio goals/i, /objectifs de portefeuille/i],
  },
};

/** Detect which RendPleX features an assistant message references.
 * Returns an ordered list of unique feature ids (max 3 chips shown). */
export function detectFeatures(text) {
  if (!text) return [];
  const found = [];
  const seen = new Set();
  for (const [id, meta] of Object.entries(ADVISOR_FEATURES)) {
    if (meta.patterns.some((re) => re.test(text))) {
      if (!seen.has(id)) {
        seen.add(id);
        found.push(id);
      }
    }
  }
  return found.slice(0, 3);
}
